import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { LoginPage } from './auth/LoginPage';
import { ForgotPasswordPage } from './auth/ForgotPasswordPage';
import { ResetPasswordPage } from './auth/ResetPasswordPage';
import { VerifyEmailPage } from './auth/VerifyEmailPage';
import { ForceChangePasswordModal } from './auth/ForceChangePasswordModal';
import { Shell } from './shell/Shell';
import { RequireSuperadmin } from './auth/RequireSuperadmin';
import { HomePage } from './routes/home/HomePage';
import { ProfilePage } from './routes/profile/ProfilePage';
import { ProjectDetailPage } from './routes/projects/ProjectDetailPage';
import { AdminHub } from './routes/admin/AdminHub';
import { AdminUsersPage } from './routes/admin/AdminUsersPage';
import { AdminProjectsPage } from './routes/admin/AdminProjectsPage';
import { CompaniesListPage } from './routes/admin/companies/CompaniesListPage';
import { CompanyDetailPage } from './routes/admin/companies/CompanyDetailPage';
import { EntryFormLayoutEditor } from './routes/admin/entry-form-layout/EntryFormLayoutEditor';
import { ContractFormLayoutEditor } from './routes/admin/contract-form-layout/ContractFormLayoutEditor';
import { TractFormLayoutEditor } from './routes/admin/tract-form-layout/TractFormLayoutEditor';
import { LeaseFormLayoutEditor } from './routes/admin/lease-form-layout/LeaseFormLayoutEditor';
import { ContractLandFormLayoutEditor } from './routes/admin/contract-land-form-layout/ContractLandFormLayoutEditor';
import { WellFormLayoutEditor } from './routes/admin/well-form-layout/WellFormLayoutEditor';
import { ObligationFormLayoutEditor } from './routes/admin/obligation-form-layout/ObligationFormLayoutEditor';
import { LeaseTractFormLayoutEditor } from './routes/admin/lease-tract-form-layout/LeaseTractFormLayoutEditor';
import { RequireApp } from './app-shell/RequireApp';
import { RunningTitleShell } from './app-shell/RunningTitleShell';
import { LandDataShell } from './app-shell/LandDataShell';

/** Old top-level Running Title paths that now live under `/a/running-title/*`.
 *  Kept as redirects so bookmarks/links to the pre-namespace URLs still work. */
const LEGACY_RT_REDIRECTS = [
  'tracts',
  'title-runs',
  'parties',
  'prospects',
  'contracts',
  'documents',
  'requirements',
] as const;

/**
 * Redirects a legacy top-level RT path (and any sub-path beneath it, e.g.
 * `/title-runs/abc123`) to the namespaced `/a/running-title/...` equivalent,
 * preserving whatever came after the base segment.
 */
function LegacyRtRedirect({ base }: { base: string }) {
  const params = useParams();
  const rest = params['*'];
  return <Navigate to={`/a/running-title/${base}${rest ? `/${rest}` : ''}`} replace />;
}

export function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Shell>
                <ForceChangePasswordModal />
                <Routes>
                  <Route index element={<HomePage />} />

                  <Route
                    path="a/running-title/*"
                    element={
                      <RequireApp appKey="running_title">
                        <RunningTitleShell />
                      </RequireApp>
                    }
                  />
                  <Route
                    path="a/land-data/*"
                    element={
                      <RequireApp appKey="land_data">
                        <LandDataShell />
                      </RequireApp>
                    }
                  />

                  {LEGACY_RT_REDIRECTS.map((path) => (
                    <Route
                      key={path}
                      path={`${path}/*`}
                      element={<LegacyRtRedirect base={path} />}
                    />
                  ))}

                  <Route
                    path="admin"
                    element={
                      <RequireSuperadmin>
                        <AdminHub />
                      </RequireSuperadmin>
                    }
                  />
                  <Route
                    path="admin/users"
                    element={
                      <RequireSuperadmin>
                        <AdminUsersPage />
                      </RequireSuperadmin>
                    }
                  />
                  <Route
                    path="admin/projects"
                    element={
                      <RequireSuperadmin>
                        <AdminProjectsPage />
                      </RequireSuperadmin>
                    }
                  />
                  <Route
                    path="admin/companies"
                    element={
                      <RequireSuperadmin>
                        <CompaniesListPage />
                      </RequireSuperadmin>
                    }
                  />
                  <Route path="admin/companies/:id" element={<CompanyDetailPage />} />
                  <Route
                    path="admin/entry-form-layout"
                    element={
                      <RequireSuperadmin>
                        <EntryFormLayoutEditor />
                      </RequireSuperadmin>
                    }
                  />
                  <Route
                    path="admin/contract-form-layout"
                    element={
                      <RequireSuperadmin>
                        <ContractFormLayoutEditor />
                      </RequireSuperadmin>
                    }
                  />
                  <Route
                    path="admin/tract-form-layout"
                    element={
                      <RequireSuperadmin>
                        <TractFormLayoutEditor />
                      </RequireSuperadmin>
                    }
                  />
                  <Route
                    path="admin/lease-form-layout"
                    element={
                      <RequireSuperadmin>
                        <LeaseFormLayoutEditor />
                      </RequireSuperadmin>
                    }
                  />
                  <Route
                    path="admin/contract-land-form-layout"
                    element={
                      <RequireSuperadmin>
                        <ContractLandFormLayoutEditor />
                      </RequireSuperadmin>
                    }
                  />
                  <Route
                    path="admin/well-form-layout"
                    element={
                      <RequireSuperadmin>
                        <WellFormLayoutEditor />
                      </RequireSuperadmin>
                    }
                  />
                  <Route
                    path="admin/obligation-form-layout"
                    element={
                      <RequireSuperadmin>
                        <ObligationFormLayoutEditor />
                      </RequireSuperadmin>
                    }
                  />
                  <Route
                    path="admin/lease-tract-form-layout"
                    element={
                      <RequireSuperadmin>
                        <LeaseTractFormLayoutEditor />
                      </RequireSuperadmin>
                    }
                  />

                  {/* Unguarded, not RT-specific (reached from /admin/projects and
                      /admin/companies/:id too) — kept top-level rather than moved
                      under /a/running-title, same as it was before namespacing. */}
                  <Route path="projects/:id" element={<ProjectDetailPage />} />

                  <Route path="profile" element={<ProfilePage />} />

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Shell>
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthProvider>
  );
}
